/**
 * @file src/offline/offlineOwner.js
 * @description Establishing whose library this is, BEFORE anything reads it.
 *
 * Both offline stores are per uid and empty themselves when the identity changes — a shared machine
 * must not keep one person's downloads for the next. That reconcile used to run as part of the app's
 * own start-up, which is comfortably after the collection boot has already read the manifest and
 * built a session from it. On a shared browser that ordering hands the second person the first
 * person's collection: their roster, their covers, their cached audio, and a library page offering
 * to delete it.
 *
 * So the two boot paths that read the library — the collection boot, and the library page — ask
 * here first. The gate itself lives in the store (`expectOwnerReconcile`), which is what every read
 * already waits on; this only makes sure it is ARMED before the first read rather than after it.
 *
 * ## It is still not awaited by the app at large
 *
 * Only the readers wait. The renderer, the audio graph and the first frame do not — the reconcile
 * is an IndexedDB open plus an auth handshake, and putting that on the path to the app existing was
 * the thing phase 1 deliberately moved off it.
 */
import { ensureFirebaseAuthFromSession, isIdentityEstablished } from '../auth/sessionAuth.js';
import { expectAudioCacheReconcile, reconcileAudioCacheOwner } from '../audio/playback/audioCacheStore.js';
import { expectOwnerReconcile, reconcileOfflineLibraryOwner } from './offlineLibraryStore.js';

let inFlight = null;

/**
 * Arm the readers' gate and settle who this browser's library belongs to.
 *
 * Idempotent per page: the handshake is shared, so calling this from the collection boot and again
 * from the app's own start-up costs one round trip, not two.
 *
 * Never rejects. A reconcile that cannot run leaves the stores as they are and releases the gate —
 * refusing to show a library is a worse failure than showing one that could not be re-checked, and
 * DELETING one on no evidence is worse than both.
 *
 * @returns {Promise<void>} resolves once the stores are safe to read.
 */
export function establishOfflineOwner() {
  if (inFlight) return inFlight;

  // Armed synchronously, before the first await: a read issued in the same tick must already be
  // waiting on this rather than sailing past an un-armed gate.
  expectAudioCacheReconcile();
  expectOwnerReconcile();

  inFlight = (async () => {
    let viewer = null;
    try {
      viewer = await ensureFirebaseAuthFromSession();
    } catch {
      viewer = null;
    }
    // Null means "nobody is signed in" only when the server said so. See `isIdentityEstablished`.
    const identityEstablished = isIdentityEstablished(viewer);
    const uid = viewer?.uid ?? null;
    await Promise.all([
      reconcileAudioCacheOwner(uid, { identityEstablished }).catch(() => {}),
      reconcileOfflineLibraryOwner(uid, { identityEstablished }).catch(() => {}),
    ]);
  })();

  return inFlight;
}
